import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { Fold } from '../../components/ui/Fold';
import { Markdown } from '../../components/ui/Markdown';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { ComponentPicker } from './ComponentPicker';
import {
  addSubstitute,
  listSubstitutes,
  removeSubstitute,
  updateSubstitute,
  type ComponentBrief,
  type SubstituteKind,
} from './api';

/**
 * Parts that stand in for this one.
 *
 * Deliberately not the merge screen, and worth keeping the difference in mind
 * while reading either. Merging says "this is one part written down twice" and
 * destroys a record; this says "these are different parts that do each other's
 * job in most designs", and both records have to survive — they have their own
 * article numbers, their own purchases and their own stock.
 *
 * Stock is shown against every entry because the question behind the list is
 * almost never "what is related to this" and almost always "this one has run
 * out, now what".
 */
export function SubstitutesPanel({
  componentId,
  onOpen,
}: {
  componentId: string;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<ComponentBrief | null>(null);
  const [kind, setKind] = useState<SubstituteKind>('drop_in');
  const [note, setNote] = useState('');
  const [mutual, setMutual] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const editable = can('catalog.write');

  const rows = useQuery({
    queryKey: ['substitutes', componentId],
    queryFn: () => listSubstitutes(componentId),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['substitutes'] });

  const create = useMutation({
    mutationFn: () =>
      addSubstitute(componentId, {
        substitute_id: (chosen as ComponentBrief).id,
        kind,
        note: note.trim() || null,
        mutual,
      }),
    onSuccess: () => {
      setAdding(false);
      setChosen(null);
      setNote('');
      setKind('drop_in');
      setMutual(true);
      setError(null);
      refresh();
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const drop = useMutation({
    mutationFn: (linkId: string) => removeSubstitute(componentId, linkId),
    onSuccess: refresh,
  });

  const retype = useMutation({
    mutationFn: (input: { linkId: string; kind: SubstituteKind }) =>
      updateSubstitute(componentId, input.linkId, { kind: input.kind }),
    onSuccess: refresh,
  });

  const entries = rows.data ?? [];

  return (
    <Fold
      title={t('substitutes.title')}
      count={rows.isLoading ? null : entries.length}
      className="panel__section substitutes"
    >
      {entries.length === 0 ? (
        <p className="panel__hint">{t('substitutes.none')}</p>
      ) : (
        <ul className="substitutes__list">
          {entries.map((row) => (
            <li key={row.id}>
              <div className="substitutes__part">
                <button type="button" className="link" onClick={() => onOpen(row.component.id)}>
                  {row.component.name}
                </button>
                <code>{row.component.internal_code}</code>
                <span
                  className={
                    Number(row.component.qty_on_hand) > 0
                      ? 'substitutes__stock is-available'
                      : 'substitutes__stock'
                  }
                >
                  {t('substitutes.onHand', { qty: num(row.component.qty_on_hand) })}
                </span>
              </div>

              <div className="substitutes__kind">
                {editable ? (
                  <select
                    value={row.kind}
                    onChange={(event) =>
                      retype.mutate({
                        linkId: row.id,
                        kind: event.target.value as SubstituteKind,
                      })
                    }
                  >
                    <option value="drop_in">{t('substitutes.kinds.drop_in')}</option>
                    <option value="conditional">{t('substitutes.kinds.conditional')}</option>
                  </select>
                ) : (
                  <span className={`badge badge--sub-${row.kind}`}>
                    {t(`substitutes.kinds.${row.kind}`)}
                  </span>
                )}
                {editable && (
                  <button type="button" className="link danger" onClick={() => drop.mutate(row.id)}>
                    {t('common.delete')}
                  </button>
                )}
              </div>

              {/* Shown in full, never truncated: "fits, but Vf is 0.2 V higher"
                  is the part that matters. That the two are related the reader
                  could have guessed. */}
              {row.note && (
                <div className="substitutes__note">
                  <Markdown source={row.note} lines={2} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable &&
        (adding ? (
          <div className="substitutes__form">
            <ComponentPicker
              label={t('substitutes.pick')}
              value={chosen}
              onChange={setChosen}
              exclude={componentId}
            />

            <label>
              {t('substitutes.kind')}
              <select value={kind} onChange={(event) => setKind(event.target.value as SubstituteKind)}>
                <option value="drop_in">{t('substitutes.kinds.drop_in')}</option>
                <option value="conditional">{t('substitutes.kinds.conditional')}</option>
              </select>
            </label>

            <label>
              {t('substitutes.note')}
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t('substitutes.notePlaceholder')}
                rows={3}
              />
              <span className="component-form__parsed">{t('common.markdownHint')}</span>
            </label>

            {/* Asked rather than assumed. Most pairs swap both ways, but the
                case this gets wrong — a wider part standing in for a narrower
                one and not the reverse — is the case that puts the wrong
                component on a board. */}
            <label className="substitutes__mutual">
              <input
                type="checkbox"
                checked={mutual}
                onChange={(event) => setMutual(event.target.checked)}
              />
              {t('substitutes.mutual')}
            </label>

            <div className="panel__actions">
              <button
                type="button"
                disabled={!chosen || create.isPending}
                onClick={() => create.mutate()}
              >
                {t('common.add')}
              </button>
              <button type="button" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </button>
            </div>

            {error && (
              <p className="panel__error" role="alert">
                {t(`substitutes.errors.${error}`, { defaultValue: t('substitutes.errors.unknown') })}
              </p>
            )}
          </div>
        ) : (
          <button type="button" onClick={() => setAdding(true)}>
            {t('substitutes.add')}
          </button>
        ))}
    </Fold>
  );
}
